import { useState, useEffect } from "react";
import Select from "react-select";
import {
  useAppServices,
  useUploadImage,
} from "../../../../../../hook/services";
import { toast } from "react-toastify";

// Options
const featureOptions = [
  { value: "Actions", label: "Actions" },
  { value: "Triggers", label: "Triggers" },
  { value: "Conversation Providers", label: "Conversation Providers" },
];

const appTypeOptions = [
  { value: "Agency", label: "Agency" },
  { value: "Sub Account", label: "Sub Account" },
];

const appInstallOptions = [
  { value: "Agency", label: "Agency" },
  { value: "Anyone", label: "Anyone" },
];

const initialFormData = {
  appId: "",
  appName: "",
  shortDescription: "",
  conversationProviderId: "",
  clientId: "",
  clientSecret: "",
  sso: "",
  funKey: "",
  featuresInclude: [],
  appType: "",
  whoCanInstall: "",
  appImage: null,
};

const EditAppsModal = ({ handleRefresh, app }) => {
  const AppService = useAppServices();
  const uploadImage = useUploadImage();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [imageChanged, setImageChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  // Prefill data
  useEffect(() => {
    if (app) setFormData(app);
  }, [app]);

  // Handlers
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setImageChanged(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleFeaturesChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      featuresInclude: selectedOptions?.map((opt) => opt.value) || [],
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setFormData((prev) => ({ ...prev, appImage: file }));
      setImageChanged(true);
    } else {
      toast.error("Please upload a valid image file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.appId || !formData.appName) {
      toast.error("App ID and App Name are required");
      return;
    }

    try {
      setLoading(true);

      let appImage = formData.appImage;

      // Upload new image if changed
      if (imageChanged && formData.appImage instanceof File) {
        const imageResponse = await uploadImage({
          file: formData.appImage,
          desiredPath: `app/logo/image`,
        });

        if (imageResponse?.response?.data) {
          appImage = imageResponse.response.data;
        }
      }

      const payload = { ...formData, appImage };

      const { response, error } = await AppService.apps.update({ payload });

      if (response) {
        toast.success("App updated successfully");
        handleClose();
        handleRefresh?.();
      } else {
        throw new Error(error?.message || "Update failed");
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // JSX
  return (
    <>
      <button
        className="btn btn-outline-primary btn-sm rounded-circle"
        onClick={handleOpen}
        title="Edit App"
      >
        <i className="bi bi-pencil"></i>
      </button>

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-light">
                <h5 className="modal-title fw-bold">Edit App</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </div>

              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  {/* Image Upload */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">App Image</label>
                    <div
                      className="border rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light position-relative"
                      style={{ cursor: "pointer", height: "200px" }}
                      onClick={() =>
                        document.getElementById("editImageInput").click()
                      }
                    >
                      {formData.appImage ? (
                        <>
                          <img
                            src={
                              formData.appImage instanceof File
                                ? URL.createObjectURL(formData.appImage)
                                : formData.appImage
                            }
                            alt="App"
                            className="img-fluid rounded"
                            style={{ maxHeight: "160px", objectFit: "contain" }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                appImage: null,
                              }));
                            }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-cloud-upload display-4 text-secondary mb-2"></i>
                          <p className="text-muted mb-0">
                            Click or drag image here
                          </p>
                        </>
                      )}
                      <input
                        type="file"
                        id="editImageInput"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                    </div>
                  </div>

                  {/* Text Fields */}
                  {[
                    ["App ID", "appId"],
                    ["App Name", "appName"],
                    ["Short Description", "shortDescription"],
                    ["Conversation Provider ID", "conversationProviderId"],
                    ["Client ID", "clientId"],
                    ["Client Secret", "clientSecret"],
                    ["SSO", "sso"],
                  ].map(([label, name]) => (
                    <div className="mb-3" key={name}>
                      <label className="form-label">{label}</label>
                      <input
                        type="text"
                        className="form-control"
                        name={name}
                        value={formData[name] || ""}
                        onChange={handleChange}
                        required={["appId", "appName"].includes(name)}
                      />
                    </div>
                  ))}

                  {/* Selects */}
                  <div className="mb-3">
                    <label className="form-label">App Type</label>
                    <Select
                      options={appTypeOptions}
                      value={
                        appTypeOptions.find(
                          (opt) => opt.value === formData.appType
                        ) || null
                      }
                      onChange={(e) => handleSelectChange(e, "appType")}
                      placeholder="Select type"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Who can install</label>
                    <Select
                      options={appInstallOptions}
                      value={
                        appInstallOptions.find(
                          (opt) => opt.value === formData.whoCanInstall
                        ) || null
                      }
                      onChange={(e) => handleSelectChange(e, "whoCanInstall")}
                      placeholder="Select option"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Features Include</label>
                    <Select
                      isMulti
                      options={featureOptions}
                      value={featureOptions.filter((opt) =>
                        formData.featuresInclude?.includes(opt.value)
                      )}
                      onChange={handleFeaturesChange}
                      placeholder="Select features"
                    />
                  </div>

                  {/* Submit */}
                  <div className="d-flex justify-content-end mt-4">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditAppsModal;
